import { useMemo } from 'react';
import snowflakeUrl from '@/assets/svg/snowflake.svg';
import raindrop1 from '@/assets/svg/raindrop-1.svg';
import raindrop2 from '@/assets/svg/raindrop-2.svg';
import raindrop3 from '@/assets/svg/raindrop-3.svg';
import type { PrecipitationType } from '@/types/weather';

const RAINDROPS = [raindrop1, raindrop2, raindrop3];

interface Props {
  precipitation: PrecipitationType;
  intensity: number;
  rainAngle: number;
  foreground?: boolean;
}

interface Particle {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number | undefined;
  variant: number;
}

function seededRandom(seed: number): number {
  const x = Math.sin(seed + 5) * 10000;
  return x - Math.floor(x);
}

// Particles travel ~125vh along their angled axis, drifting horizontally by
// D = 125 * sin(angle). A particle at the downwind edge at screen bottom must
// have spawned from upwind of that edge by D, so the spawn range extends past
// the upwind edge. For +angle (leftward drift in CSS), upwind is the right.
function rainSpawnRange(angleDeg: number): { start: number; span: number } {
  const drift = 125 * Math.sin((angleDeg * Math.PI) / 180); // approx % of viewport
  const margin = 20; // buffer so edges stay populated under any aspect ratio
  const extra = Math.abs(drift) + margin;
  if (drift >= 0) return { start: 0, span: 100 + extra };       // extend right
  return { start: -extra, span: 100 + extra };                    // extend left
}

function generateRainParticles(count: number, baseDuration: number, angleDeg: number): Particle[] {
  const { start, span } = rainSpawnRange(angleDeg);
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: start + seededRandom(i * 7) * span,
    delay: -(seededRandom(i * 11) * baseDuration),
    duration: baseDuration + seededRandom(i * 17) * 0.3,
    size: 6 + seededRandom(i * 29) * 4,
    variant: Math.floor(seededRandom(i * 41) * RAINDROPS.length),
  }));
}

function generateSnowflakes(count: number): Particle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    left: seededRandom(i * 9) * 100,
    delay: -(seededRandom(i * 13) * 8),
    duration: 5 + seededRandom(i * 19) * 5,
    size: 12 + seededRandom(i * 23) * 20,
    variant: 0,
  }));
}

export function PrecipitationLayer({ precipitation, intensity, rainAngle, foreground = false }: Props) {
  // Scale particle count by spawn-range width so density stays constant when angled.
  const densityScale = (rainSpawnRange(rainAngle).span) / 100;

  // Foreground: fewer but larger/faster particles for depth-of-field feel.
  const countMult = foreground ? 0.25 : 1;
  const sizeMult = foreground ? 2.2 : 1;
  const durMult = foreground ? 0.55 : 1;

  const rainParticles = useMemo<Particle[]>(() => {
    if (precipitation !== 'rain' && precipitation !== 'heavy-rain' && precipitation !== 'drizzle') return [];
    const base = precipitation === 'drizzle' ? 40 : precipitation === 'rain' ? 80 : 160;
    const dur = (precipitation === 'drizzle' ? 1.2 : precipitation === 'rain' ? 0.75 : 0.5) * durMult;
    const particles = generateRainParticles(Math.round(base * intensity * densityScale * countMult), dur, rainAngle);
    return particles.map((p) => ({ ...p, size: (p.size ?? 12) * sizeMult }));
  }, [precipitation, intensity, rainAngle, densityScale, countMult, sizeMult, durMult]);

  const sleetRain = useMemo<Particle[]>(() => {
    if (precipitation !== 'sleet') return [];
    const particles = generateRainParticles(Math.round(60 * intensity * densityScale * countMult), 0.8 * durMult, rainAngle);
    return particles.map((p) => ({ ...p, size: (p.size ?? 10) * sizeMult }));
  }, [precipitation, intensity, rainAngle, densityScale, countMult, sizeMult, durMult]);

  const sleetSnow = useMemo<Particle[]>(() => {
    if (precipitation !== 'sleet') return [];
    const particles = generateSnowflakes(Math.round(15 * intensity * countMult));
    return particles.map((p) => ({ ...p, size: (p.size ?? 14) * sizeMult, duration: p.duration * durMult }));
  }, [precipitation, intensity, countMult, sizeMult, durMult]);

  const snowParticles = useMemo<Particle[]>(() => {
    if (precipitation !== 'snow') return [];
    const particles = generateSnowflakes(Math.round(60 * intensity * countMult));
    return particles.map((p) => ({ ...p, size: (p.size ?? 14) * sizeMult, duration: p.duration * durMult }));
  }, [precipitation, intensity, countMult, sizeMult, durMult]);

  const visible = precipitation !== 'none';

  return (
    <div
      className="layer"
      style={{
        zIndex: foreground ? 85 : 60,
        opacity: visible ? 1 : 0,
        transition: 'opacity 2s ease-in-out',
        // CSS custom property inherited by all rain particles for the wind angle
        ['--rain-angle' as string]: `${rainAngle}deg`,
      }}
    >
      {/* Rain */}
      {rainParticles.map((p) => (
        <img
          key={p.id}
          src={RAINDROPS[p.variant] ?? RAINDROPS[0]!}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: `${p.size ?? 12}px`,
            height: 'auto',
            animationName: 'rainFall',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
          }}
        />
      ))}

      {/* Sleet — rain component */}
      {sleetRain.map((p) => (
        <img
          key={`sr-${p.id}`}
          src={RAINDROPS[p.variant] ?? RAINDROPS[0]!}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: `${p.size ?? 10}px`,
            height: 'auto',
            opacity: 0.85,
            animationName: 'rainFall',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: 'linear',
            animationIterationCount: 'infinite',
          }}
        />
      ))}

      {/* Sleet — snow component */}
      {sleetSnow.map((p) => (
        <img
          key={`ss-${p.id}`}
          src={snowflakeUrl}
          alt=""
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: `${p.size ?? 14}px`,
            height: `${p.size ?? 14}px`,
            animationName: 'snowFall',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: 'ease-in',
            animationIterationCount: 'infinite',
          }}
        />
      ))}

      {/* Snow */}
      {snowParticles.map((p) => (
        <img
          key={`sn-${p.id}`}
          src={snowflakeUrl}
          alt=""
          style={{
            position: 'absolute',
            left: `${p.left}%`,
            top: 0,
            width: `${p.size ?? 14}px`,
            height: `${p.size ?? 14}px`,
            animationName: 'snowFall',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: 'ease-in',
            animationIterationCount: 'infinite',
          }}
        />
      ))}
    </div>
  );
}
